/** Authenticated read models. No raw RTDB fallback is permitted. */
import { z } from 'zod'
import {
  subscribeInvalidation,
  type HintSurface,
} from './dashboard-invalidation'
import type { Database } from 'firebase/database'
import { getEnvironment } from '../lib/environment'
import { createApiClient, isTransientApiError } from './api/client'
import { getFirebaseServices } from './firebase'
import { acceptVisibilityScope, visibilityEpoch } from './visibility-cache'

export type Unsubscribe = () => void
export type Constraint = readonly [
  string,
  string | number | boolean | null,
  string?,
]
type View = {
  path: string
  constraints: readonly Constraint[]
  search?: string
}
type Snapshot = { exists: () => boolean; val: () => unknown }

export const ref = (_database: Database, path: string): View => ({
  path,
  constraints: [],
})
export const query = (view: View, ...constraints: Constraint[]): View => ({
  ...view,
  constraints,
})
// A Recent conversations page holding only the rows that match `text`, read
// from the clinic's whole list. The text travels in the request body.
export const withSearch = (view: View, text: string): View => ({
  ...view,
  search: text,
})
export const orderByChild = (field: string): Constraint => [
  'orderByChild',
  field,
]
export const limitToFirst = (size: number): Constraint => ['limitToFirst', size]
export const limitToLast = (size: number): Constraint => ['limitToLast', size]
export const startAt = (
  value: number | string | boolean,
  key?: string
): Constraint =>
  key === undefined ? ['startAt', value] : ['startAt', value, key]
export const startAfter = (value: number | string, key?: string): Constraint =>
  key === undefined ? ['startAfter', value] : ['startAfter', value, key]
export const endAt = (
  value: number | string | null,
  key?: string
): Constraint => (key === undefined ? ['endAt', value] : ['endAt', value, key])
export const endBefore = (value: number | string, key?: string): Constraint =>
  key === undefined ? ['endBefore', value] : ['endBefore', value, key]

// A transient failure (isTransientApiError) is retried once after a second.
// Access refusals and invalid answers are never retried here.
const READ_RETRY_DELAY_MS = 1_000
// A subscription keeps its last good value through transient failures for
// this long from the first of them, retrying within seconds, before it reports
// the failure. A first read with no value yet reports it after this many
// attempts.
const READ_FAILURE_GRACE_MS = 120_000
const FIRST_READ_ATTEMPTS = 3
const TRANSIENT_RETRY_MS = [2_000, 5_000, 15_000, 30_000] as const

const jittered = (delayMs: number) =>
  Math.round(delayMs * (0.8 + Math.random() * 0.4))

// A versioned view ("<root>/v<n>/<tenant>") names its tenant after the version.
function viewTenant(parts: string[]) {
  return /^v[0-9]+$/.test(parts[1] ?? '') ? parts[2] : parts[1]
}

export async function get(view: View): Promise<Snapshot> {
  const user = getFirebaseServices().auth.currentUser
  const epoch = visibilityEpoch()
  const parts = view.path.split('/')
  const tenant = viewTenant(parts)
  if (!tenant) throw new Error('Invalid Dashboard read view')
  const api = createApiClient({
    baseUrl: getEnvironment().userManagementBaseUrl,
    getUser: () => getFirebaseServices().auth.currentUser,
  })
  const read = () =>
    api.request(
      `/dashboard/clinics/${encodeURIComponent(tenant)}/read-view?contextVersion=2&interactionFacts=1`,
      {
        method: 'POST',
        body: view,
        responseSchema: z
          .object({ value: z.unknown(), visibilityScope: z.string().min(1) })
          .strict(),
      }
    )
  const discarded = () =>
    user?.uid !== getFirebaseServices().auth.currentUser?.uid ||
    epoch !== visibilityEpoch()
  let result: Awaited<ReturnType<typeof read>>
  try {
    result = await read()
  } catch (reason) {
    if (!isTransientApiError(reason)) throw reason
    await new Promise((resolve) =>
      setTimeout(resolve, jittered(READ_RETRY_DELAY_MS))
    )
    if (discarded()) {
      throw new Error('Dashboard visibility changed; read discarded')
    }
    result = await read()
  }
  if (discarded()) {
    throw new Error('Dashboard visibility changed; read discarded')
  }
  acceptVisibilityScope(result.visibilityScope)
  return {
    exists: () => result.value !== null && result.value !== undefined,
    val: () => result.value,
  }
}

// Hints invalidate authenticated reads; periodic reads only reconcile missed hints.
// Every request re-evaluates server authority. No raw conversation subscription.
// A transient failure keeps the last good value within READ_FAILURE_GRACE_MS.
export function onValue(
  view: View,
  value: (snapshot: Snapshot) => void,
  error: (reason: unknown) => void
): Unsubscribe {
  let cancelled = false
  let running = false
  let failures = 0
  let transientFailure = false
  let delivered = false
  let failingSinceMs: number | null = null
  let hintsHealthy = false
  let dirty = false
  let timer: ReturnType<typeof setTimeout> | undefined
  const active = () =>
    document.visibilityState !== 'hidden' && navigator.onLine !== false
  const poll = async () => {
    if (cancelled || !active()) return
    if (running) return
    clearTimeout(timer)
    running = true
    try {
      const snapshot = await get(view)
      failures = 0
      transientFailure = false
      failingSinceMs = null
      delivered = true
      if (!cancelled) value(snapshot)
    } catch (reason) {
      failures += 1
      failingSinceMs ??= Date.now()
      transientFailure = isTransientApiError(reason)
      const retained =
        transientFailure &&
        (delivered
          ? Date.now() - failingSinceMs < READ_FAILURE_GRACE_MS
          : failures < FIRST_READ_ATTEMPTS)
      if (!cancelled && !retained) {
        value({ exists: () => false, val: () => null })
        error(reason)
      }
    } finally {
      running = false
      if (dirty && failures === 0 && !cancelled && active()) {
        dirty = false
        void poll()
      } else if (!cancelled && active())
        timer = setTimeout(
          () => {
            void poll()
          },
          transientFailure
            ? jittered(
                TRANSIENT_RETRY_MS[
                  Math.min(failures, TRANSIENT_RETRY_MS.length) - 1
                ]!
              )
            : Math.min(
                (hintsHealthy ? 120000 : 30000) * 2 ** Math.min(failures, 3),
                240000
              )
        )
    }
  }
  const resume = () => {
    clearTimeout(timer)
    void poll()
  }
  document.addEventListener('visibilitychange', resume)
  window.addEventListener('online', resume)
  window.addEventListener('offline', resume)
  const parts = view.path.split('/')
  const tenant = viewTenant(parts)
  // Each hinted view wakes only for its own surfaces: Overview's frequent
  // refreshes never re-read conversations, and conversation writes never
  // re-read Overview.
  const surfaces: readonly HintSurface[] | undefined =
    parts[0] === 'dashboardOverview'
      ? ['overview']
      : [
            'patients',
            'threads',
            'messages',
            'calls',
            'dashboardCallTimeline',
            'dashboardInteractionAggregates',
          ].includes(parts[0] ?? '')
        ? ['conversations', 'aggregates']
        : undefined
  const stopHints =
    tenant && surfaces
      ? subscribeInvalidation(
          tenant,
          (healthy) => {
            hintsHealthy = healthy
            if (failures > 0) return
            if (running) dirty = true
            else void poll()
          },
          surfaces
        )
      : () => undefined
  void poll()
  return () => {
    stopHints()
    cancelled = true
    clearTimeout(timer)
    document.removeEventListener('visibilitychange', resume)
    window.removeEventListener('online', resume)
    window.removeEventListener('offline', resume)
  }
}
